import { MessagesPaths } from 'app/configs/routesConfig/messages';
import { PhonePaths } from 'app/configs/routesConfig/phone';

import { NAVIGATION_ITEM_TYPE } from 'constants/navigation';

import { TAB_ID } from '../MobileTabsProvider/MobileTabsProvider.constants';

import {
  BOTTOM_NAV_ICONS,
  BOTTOM_NAV_LABELS,
  CREATE_ITEM_ID,
  MESSAGES_ITEM_ID,
  MORE_ITEM_ID,
  NOTIFICATIONS_ITEM_ID,
  PHONE_ITEM_ID,
  PROFILE_NAV_ITEM,
} from './BottomNav.constants';
import { HOME_GROUP_MEMBER_IDS, NAV_MEMBER_KIND } from './BottomNav.groups.constants';

/**
 * Synthetic "More" tab placeholder, shown when any permitted destination falls outside the
 * primary allowlist (so the rest — incl. the full settings tree for global-company users — stays
 * reachable).
 */
const MORE_TAB = {
  id: MORE_ITEM_ID,
  title: 'More',
  type: NAVIGATION_ITEM_TYPE.GROUP,
};

/** Synthetic Messages tab — not a `navigationConfig` entry; gated by `showMessages`. */
const MESSAGES_NAV_ITEM = {
  id: MESSAGES_ITEM_ID,
  title: 'Messages',
  type: NAVIGATION_ITEM_TYPE.GROUP,
};

/** Synthetic Notifications tab — not a `navigationConfig` entry; gated by `showNotifications`. */
const NOTIFICATIONS_NAV_ITEM = {
  id: NOTIFICATIONS_ITEM_ID,
  title: 'Notifications',
  type: NAVIGATION_ITEM_TYPE.GROUP,
};

/** Synthetic Phone tab — not a `navigationConfig` entry; gated by `showPhone` (telephony claim). */
const PHONE_NAV_ITEM = {
  id: PHONE_ITEM_ID,
  title: 'Phone',
  type: NAVIGATION_ITEM_TYPE.GROUP,
};

/** Synthetic Create tab — not a `navigationConfig` entry; gated by `showCreate` (per-object create permission). */
const CREATE_NAV_ITEM = {
  id: CREATE_ITEM_ID,
  title: 'Create',
  type: NAVIGATION_ITEM_TYPE.GROUP,
};

/**
 * Builds the bottom-nav tabs from the already permission-filtered nav items and the
 * gating booleans for the two synthetic action tabs (Messages, Notifications).
 *
 *  - Primary tabs = the BOTTOM_NAV_PRIMARY allowlist (the ceiling), in order, limited to the ones
 *    the user is actually permitted to see (present in `available`).
 *  - Synthetic action tabs (Messages, Notifications) are appended after the primary tabs when their
 *    respective gating flag is true.
 *  - Profile (every authenticated user has one) and every OTHER permitted destination overflow into
 *    "More" — Profile is appended last so it sits at the bottom of the More sheet.
 *
 * @param {Array<Object>} available - already permission-filtered top-level nav items
 * @param {Object} options - gating options for synthetic action tabs
 * @param {boolean} [options.showMessages] - whether to show the Messages action tab
 * @param {boolean} [options.showNotifications] - whether to show the Notifications action tab
 * @returns {{ visibleTabs: Array<Object>, overflowItems: Array<Object> }}
 */
export const computeVisibleTabs = (
  available,
  { showMessages = false, showNotifications = false } = {},
) => {
  const BOTTOM_NAV_PRIMARY = ['pipelines', 'dashboards'];
  const items = available ?? [];

  const primaryTabs = BOTTOM_NAV_PRIMARY.map(id => items.find(item => item.id === id)).filter(
    Boolean,
  );

  const syntheticTabs = [
    ...getConditionalSyntheticTab(showMessages, MESSAGES_NAV_ITEM),
    ...getConditionalSyntheticTab(showNotifications, NOTIFICATIONS_NAV_ITEM),
  ];

  const overflowItems = [
    ...items.filter(item => !BOTTOM_NAV_PRIMARY.includes(item.id)),
    PROFILE_NAV_ITEM,
  ];

  const visibleTabs = [...primaryTabs, ...syntheticTabs, MORE_TAB];

  return { visibleTabs, overflowItems };
};

/**
 * Returns a single-element array with the tab when condition is true, or an empty array.
 * Avoids conditional spreading with a ternary on an array.
 *
 * @param {boolean} condition
 * @param {Object} tab
 * @returns {Array<Object>}
 */
const getConditionalSyntheticTab = (condition, tab) => (condition ? [tab] : []);

/**
 * Flattens the nav tree to a list of navigable destinations (ITEMs only).
 * Recursively traverses GROUP and COLLAPSE children.
 *
 * @param {Array<Object>} items - nav items (may be nested)
 * @returns {Array<Object>} flat list of ITEM-type nav nodes
 */
export const flattenNavItems = items => {
  if (!items) {
    return [];
  }

  return items.reduce((acc, item) => {
    if (item.type === NAVIGATION_ITEM_TYPE.ITEM && item.url) {
      acc.push(item);

      return acc;
    }

    if (item.children) {
      acc.push(...flattenNavItems(item.children));
    }

    return acc;
  }, []);
};

/**
 * Filters a flat list of nav destinations by a search query (case-insensitive title match).
 *
 * @param {Array<Object>} items - flat nav destinations
 * @param {string} query - search string
 * @returns {Array<Object>} matching items
 */
export const filterNavItems = (items, query) => {
  if (!query || !query.trim()) {
    return items;
  }

  const lower = query.toLowerCase();

  return items.filter(item => item.title && item.title.toLowerCase().includes(lower));
};

// ─── Group taxonomy ───────────────────────────────────────────────────────────

/**
 * @typedef {Object} NavGroupMember
 * @property {string} id - unique member id
 * @property {string} label - display label
 * @property {string} kind - NAV_MEMBER_KIND.ROUTE | NAV_MEMBER_KIND.ACTION
 * @property {string|null} url - route URL (null for action members)
 * @property {string} actionId - action identifier for action members (empty string for route members)
 * @property {Object|null} icon - CDS icon component (may be null)
 */

/**
 * @typedef {Object} NavGroup
 * @property {string} id - group id (NAV_GROUP_ID value)
 * @property {string} label - tab label shown in the bar
 * @property {boolean} isGroup - true when there are 2+ available members (shows dot-cue + hold)
 * @property {NavGroupMember} primary - the member navigated/actioned on a plain tap
 * @property {Array<NavGroupMember>} members - all available members (shown in the card)
 * @property {number} badgeCount - aggregate unread count (0 = hidden)
 */

/**
 * Builds the groups-based tab row for the grouped nav redesign.
 *
 * Final bar order: Pipelines · Home · Comms · Pricing · More
 *
 * - Pipelines: plain tab (single, ROUTE). Omitted if user lacks pipelines permission.
 * - Home: expandable group (Home + Dashboards + Reports, all ROUTE). Members filtered to
 *   what the user has. Collapses to a plain tab if only 1 member is available.
 * - Comms: expandable group (Messages ACTION + Notifications ACTION). Filtered to gating booleans.
 *   Omitted entirely if neither is available.
 * - Pricing: plain tab (single, ROUTE). Omitted if user lacks pricingWorkbench permission.
 * - More: the existing MoreSheet overflow — always present.
 *
 * @param {Array<Object>} available - permission-filtered top-level nav items from Redux
 * @param {Object} options
 * @param {boolean} [options.showMessages] - Messages tab gating
 * @param {boolean} [options.showNotifications] - Notifications tab gating
 * @param {boolean} [options.showPhone] - Phone (telephony) member gating
 * @param {boolean} [options.showCreate] - Create ("+") tab gating
 * @param {number} [options.messagesUnreadCount] - unread count for Messages badge
 * @param {number} [options.notificationsUnreadCount] - unread count for Notifications badge
 * @returns {{ groups: Array<NavGroup>, overflowItems: Array<Object> }}
 */
export const computeVisibleGroups = (
  available,
  {
    showMessages = false,
    showNotifications = false,
    showPhone = false,
    showCreate = false,
    messagesUnreadCount = 0,
    notificationsUnreadCount = 0,
  } = {},
) => {
  const items = available ?? [];
  const byId = id => items.find(item => item.id === id) ?? null;

  // IDs consumed by the primary bar — everything else overflows into More
  const CONSUMED_IDS = new Set(['pipelines', 'home', 'dashboards', 'reports', 'pricingWorkbench']);

  const groups = [];

  // ── Pipelines (plain tab) ──────────────────────────────────────────────────
  const pipelinesItem = byId('pipelines');

  if (pipelinesItem) {
    groups.push(buildSingleRouteGroup('pipelines', 'Pipeline', pipelinesItem));
  }

  // ── Home group (Home + Dashboards + Reports) ───────────────────────────────
  const homeMembers = HOME_GROUP_MEMBER_IDS.map(id => byId(id))
    .filter(Boolean)
    .map(navItem => buildRouteMember(navItem));

  if (homeMembers.length > 0) {
    groups.push({
      id: 'home',
      label: 'Home',
      isGroup: homeMembers.length > 1,
      primary: homeMembers[0],
      members: homeMembers,
      badgeCount: 0,
    });
  }

  // ── Create ("+") tab (single ACTION member → opens the create bottom sheet) ─
  if (showCreate) {
    const createMember = buildActionMember(CREATE_NAV_ITEM, 0);

    groups.push({
      id: CREATE_ITEM_ID,
      label: BOTTOM_NAV_LABELS[CREATE_ITEM_ID],
      isGroup: false,
      primary: createMember,
      members: [createMember],
      badgeCount: 0,
    });
  }

  // ── Comms group (Messages [ROUTE → /messages page] + Notifications [ACTION sheet] +
  //    Phone [ROUTE → /phone full-screen telephony page]) ─────────
  const commsMembers = [
    ...getConditionalSyntheticTab(showMessages, buildMessagesRouteMember(messagesUnreadCount)),
    ...getConditionalSyntheticTab(
      showNotifications,
      buildActionMember(NOTIFICATIONS_NAV_ITEM, notificationsUnreadCount),
    ),
    ...getConditionalSyntheticTab(showPhone, buildPhoneRouteMember()),
  ];

  if (commsMembers.length > 0) {
    const commsBadge = (messagesUnreadCount ?? 0) + (notificationsUnreadCount ?? 0);
    const isCommsGroup = commsMembers.length > 1;

    groups.push({
      id: 'comms',
      label: 'Comms',
      isGroup: isCommsGroup,
      // Comms is a peer group (Messages / Notifications have no single "primary") — a tap should
      // open the selection card, NOT auto-fire one of them. A single-member Comms is a plain tab.
      tapOpensCard: isCommsGroup,
      primary: commsMembers[0],
      members: commsMembers,
      badgeCount: commsBadge,
    });
  }

  // ── Pricing (plain tab) ────────────────────────────────────────────────────
  const pricingItem = byId('pricingWorkbench');

  if (pricingItem) {
    groups.push(buildSingleRouteGroup('pricing', 'Pricing', pricingItem));
  }

  // ── More tab ───────────────────────────────────────────────────────────────
  groups.push({ ...MORE_TAB, isGroup: false, primary: null, members: [], badgeCount: 0 });

  // ── Overflow (everything not consumed) ────────────────────────────────────
  const overflowItems = [...items.filter(item => !CONSUMED_IDS.has(item.id)), PROFILE_NAV_ITEM];

  return { groups, overflowItems };
};

/**
 * Builds a single-member (plain) group for a route nav item.
 *
 * @param {string} groupId
 * @param {string} label
 * @param {Object} navItem
 * @returns {NavGroup}
 */
const buildSingleRouteGroup = (groupId, label, navItem) => {
  const member = buildRouteMember(navItem);

  return {
    id: groupId,
    label,
    isGroup: false,
    primary: member,
    members: [member],
    badgeCount: 0,
  };
};

/**
 * Converts a nav item config into a ROUTE-kind member descriptor.
 *
 * @param {Object} navItem
 * @returns {NavGroupMember}
 */
const buildRouteMember = navItem => ({
  id: navItem.id,
  label: BOTTOM_NAV_LABELS[navItem.id] ?? navItem.title,
  kind: NAV_MEMBER_KIND.ROUTE,
  url: navItem.url ?? null,
  actionId: '',
  icon: BOTTOM_NAV_ICONS[navItem.id] ?? navItem.icon ?? null,
  badgeCount: 0,
});

/**
 * Converts a synthetic action tab config into an ACTION-kind member descriptor.
 *
 * @param {Object} syntheticItem - MESSAGES_NAV_ITEM or NOTIFICATIONS_NAV_ITEM
 * @param {number} badgeCount
 * @returns {NavGroupMember}
 */
const buildActionMember = (syntheticItem, badgeCount) => ({
  id: syntheticItem.id,
  label: BOTTOM_NAV_LABELS[syntheticItem.id] ?? syntheticItem.title,
  kind: NAV_MEMBER_KIND.ACTION,
  url: null,
  actionId: syntheticItem.id,
  icon: BOTTOM_NAV_ICONS[syntheticItem.id] ?? null,
  badgeCount: badgeCount ?? 0,
});

/**
 * Messages member — a ROUTE to the dedicated `/messages` page (full chat surface, responsive),
 * NOT a bottom-sheet overlay, while still carrying the team-chat unread badge.
 *
 * @param {number} badgeCount
 * @returns {NavGroupMember}
 */
const buildMessagesRouteMember = badgeCount => ({
  id: MESSAGES_ITEM_ID,
  label: BOTTOM_NAV_LABELS[MESSAGES_ITEM_ID] ?? MESSAGES_NAV_ITEM.title,
  kind: NAV_MEMBER_KIND.ROUTE,
  url: MessagesPaths.messages.path,
  actionId: '',
  icon: BOTTOM_NAV_ICONS[MESSAGES_ITEM_ID] ?? null,
  badgeCount: badgeCount ?? 0,
});

/**
 * Phone member — a ROUTE to the dedicated `/phone` full-screen telephony page (a destination that
 * coexists with the bottom nav), NOT a Drawer overlay.
 *
 * @returns {NavGroupMember}
 */
/**
 * Maps a nav member to the keep-alive tab id whose stack it belongs to. Phone and Messages have
 * their own tab stacks; every other member belongs to its group's tab.
 *
 * @param {NavGroupMember} member
 * @param {string} groupId
 * @returns {string}
 */
export const getMemberTabId = (member, groupId) => {
  if (member?.id === PHONE_ITEM_ID) {
    return TAB_ID.PHONE;
  }

  if (member?.id === MESSAGES_ITEM_ID) {
    return TAB_ID.COMMS;
  }

  return groupId;
};

const buildPhoneRouteMember = () => ({
  id: PHONE_ITEM_ID,
  label: BOTTOM_NAV_LABELS[PHONE_ITEM_ID] ?? PHONE_NAV_ITEM.title,
  kind: NAV_MEMBER_KIND.ROUTE,
  url: PhonePaths.phone.path,
  actionId: '',
  icon: BOTTOM_NAV_ICONS[PHONE_ITEM_ID] ?? null,
  badgeCount: 0,
});
